import type { MyEntitiesResponse } from '@/lib/api/client-portal.api'
import { clientPortalApi } from '@/lib/api/client-portal.api'

import { isValidUUID } from '@scrambl/shared'

import { PortalMetadataContractError } from './metadata.api'

export interface PortalDirectoryContext {
  userId: string
  signal: AbortSignal
}
export const portalDirectoryKeys = {
  root: ['client-portal', 'owned-directory'] as const,
  list: (workspaceSession: unknown) =>
    ['client-portal', 'owned-directory', workspaceSession] as const,
}
const fail = (): never => {
  throw new PortalMetadataContractError()
}
export const portalDirectoryApi = {
  async read(context: PortalDirectoryContext): Promise<MyEntitiesResponse> {
    context.signal.throwIfAborted()
    if (!isValidUUID(context.userId)) fail()
    const body = await clientPortalApi.getMyEntities({ signal: context.signal })
    context.signal.throwIfAborted()
    if (
      !body ||
      body.userId !== context.userId ||
      !Array.isArray(body.entities)
    )
      fail()
    const ids = new Set<string>()
    for (const entity of body.entities) {
      if (
        !entity ||
        !isValidUUID(entity.entityId) ||
        ids.has(entity.entityId) ||
        typeof entity.entityName !== 'string' ||
        !isValidUUID(entity.organization?.id) ||
        typeof entity.organization.name !== 'string' ||
        (entity.organization.logoUrl != null &&
          typeof entity.organization.logoUrl !== 'string') ||
        !Number.isSafeInteger(entity.unreadAttentionCount) ||
        entity.unreadAttentionCount < 0
      )
        fail()
      ids.add(entity.entityId)
    }
    return {
      userId: context.userId,
      entities: body.entities.map(entity => ({
        entityId: entity.entityId,
        entityName: entity.entityName,
        organization: {
          id: entity.organization.id,
          name: entity.organization.name,
          logoUrl: entity.organization.logoUrl,
        },
        unreadAttentionCount: entity.unreadAttentionCount,
      })),
    }
  },
}
